/**
 * 提示词 @引用编辑器：contentEditable 芯片化输入（对齐首页 PromptInput 的 chip 方案）。
 *
 * - `@图N` 标记渲染为不可拆分的标签（chip，含缩略图），退格选中即整片删除；
 * - 键入 `@` 触发自动补全下拉（选项为当前有图上游），输入即时过滤，
 *   方向键/回车选择、Esc 关闭、鼠标悬停点击可选；
 * - 序列化仍为纯文本 `@图N `（chip 与文本互转），与提交解析器 atReference 兼容；
 * - 粘贴按纯文本插入；光标不在编辑器内时外部入口（「引用」弹层）插入到末尾。
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface PromptMentionOption {
  refNo: number;
  /** 缩略图可显示地址（已解析媒体令牌） */
  thumbnail?: string;
  isGroup?: boolean;
  /** 图片组的张数角标 */
  count?: number;
}

export interface PromptMentionEditorHandle {
  /** 在光标处插入 @图N（光标不在编辑器内则追加到末尾），供「引用」弹层等外部入口调用 */
  insertMention: (refNo: number) => void;
}

interface PromptMentionEditorProps {
  value: string;
  onChange: (value: string) => void;
  options: PromptMentionOption[];
  placeholder?: string;
  className?: string;
  /** 外层 relative 容器类名（便于在 flex 布局中撑满高度） */
  wrapperClassName?: string;
}

interface MentionState {
  /** @片段所在文本节点与起点偏移（插入时据此替换该片段） */
  node: Text;
  offset: number;
  query: string;
  selected: number;
}

const TOKEN_RE = /@(?:图|image|img)\s*(\d+)/gi;

function buildChip(refNo: number, options: PromptMentionOption[]): HTMLSpanElement {
  const option = options.find((o) => o.refNo === refNo);
  const chip = document.createElement('span');
  chip.setAttribute('contenteditable', 'false');
  chip.setAttribute('data-ref-no', String(refNo));
  chip.className =
    'inline-flex items-center gap-1 mx-0.5 px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 text-xs align-middle select-none border border-blue-100';
  chip.style.cursor = 'default';

  if (option?.thumbnail) {
    const img = document.createElement('img');
    img.src = option.thumbnail;
    img.alt = '';
    img.className = 'w-4 h-4 rounded object-cover';
    chip.appendChild(img);
  }
  const label = document.createElement('span');
  label.textContent = `图${refNo}`;
  chip.appendChild(label);
  return chip;
}

export const PromptMentionEditor = forwardRef<PromptMentionEditorHandle, PromptMentionEditorProps>(
  function PromptMentionEditor(
    { value, onChange, options, placeholder, className, wrapperClassName },
    ref
  ) {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const lastSyncedTextRef = useRef('');
    const [mention, setMention] = useState<MentionState | null>(null);

    /** 把纯文本（含 @图N）渲染成 DOM：标记 → chip，其余原样 */
    const renderToEditor = useCallback(
      (text: string) => {
        const editor = editorRef.current;
        if (!editor) return;
        while (editor.firstChild) editor.removeChild(editor.firstChild);

        let lastIndex = 0;
        let match: RegExpExecArray | null;
        const re = new RegExp(TOKEN_RE.source, TOKEN_RE.flags);
        while ((match = re.exec(text)) !== null) {
          if (match.index > lastIndex) {
            editor.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
          }
          editor.appendChild(buildChip(parseInt(match[1], 10), options));
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) {
          editor.appendChild(document.createTextNode(text.slice(lastIndex)));
        }
        lastSyncedTextRef.current = text;
      },
      [options]
    );

    useEffect(() => {
      if (value === lastSyncedTextRef.current) return;
      renderToEditor(value);
    }, [value, renderToEditor]);

    /** 编辑器 DOM → 纯文本（chip 序列化为 @图N，统一规范写法） */
    const serializeEditor = useCallback((): string => {
      const editor = editorRef.current;
      if (!editor) return '';
      let result = '';
      editor.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          result += node.textContent || '';
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;
          const refNo = el.getAttribute('data-ref-no');
          if (refNo) result += `@图${refNo}`;
          else result += el.textContent || '';
        }
      });
      return result;
    }, []);

    /** 检查光标前是否处于 "@片段" 输入态 */
    const detectMention = useCallback(() => {
      const editor = editorRef.current;
      if (!editor || options.length === 0) {
        setMention(null);
        return;
      }
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount || !sel.isCollapsed) {
        setMention(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const container = range.startContainer;
      if (container.nodeType !== Node.TEXT_NODE || !editor.contains(container)) {
        setMention(null);
        return;
      }
      const textNode = container as Text;
      const before = textNode.textContent?.slice(0, range.startOffset) ?? '';
      const m = /@([^\s@]*)$/.exec(before);
      if (m) {
        setMention({ node: textNode, offset: range.startOffset - m[0].length, query: m[1], selected: 0 });
      } else {
        setMention(null);
      }
    }, [options.length]);

    const handleInput = useCallback(() => {
      const text = serializeEditor();
      lastSyncedTextRef.current = text;
      onChange(text);
      detectMention();
    }, [serializeEditor, onChange, detectMention]);

    /** 在当前光标处插入 chip + 空格，光标落在空格后 */
    const insertChipAtCaret = useCallback(
      (refNo: number) => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.focus();
        const chip = buildChip(refNo, options);
        const space = document.createTextNode(' ');

        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
          const range = sel.getRangeAt(0);
          range.deleteContents();
          range.insertNode(space);
          range.insertNode(chip);
          range.setStartAfter(space);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        } else {
          editor.appendChild(chip);
          editor.appendChild(space);
          const range = document.createRange();
          range.setStartAfter(space);
          range.collapse(true);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      },
      [options]
    );

    /** 补全确认：移除输入中的 "@片段" 后在原位插入 chip */
    const applyMention = useCallback(
      (refNo: number) => {
        if (mention) {
          const rest = mention.node.textContent ?? '';
          mention.node.textContent =
            rest.slice(0, mention.offset) + rest.slice(mention.offset + 1 + mention.query.length);
          // 光标移到片段删除后的位置，chip 插在该处
          const sel = window.getSelection();
          const range = document.createRange();
          range.setStart(mention.node, mention.offset);
          range.collapse(true);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
        insertChipAtCaret(refNo);
        setMention(null);
        handleInput();
      },
      [mention, insertChipAtCaret, handleInput]
    );

    useImperativeHandle(
      ref,
      () => ({
        insertMention(refNo: number) {
          insertChipAtCaret(refNo);
          handleInput();
        },
      }),
      [insertChipAtCaret, handleInput]
    );

    const filteredOptions = (() => {
      if (!mention) return [];
      const q = mention.query.toLowerCase();
      return options.filter((o) => {
        if (!q) return true;
        const digits = q.replace(/^(图|image|img)/, '');
        return `图${o.refNo}`.includes(q) || (digits !== '' && String(o.refNo).startsWith(digits));
      });
    })();

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (mention && filteredOptions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMention((m) => (m ? { ...m, selected: (m.selected + 1) % filteredOptions.length } : m));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMention((m) =>
            m ? { ...m, selected: (m.selected - 1 + filteredOptions.length) % filteredOptions.length } : m
          );
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          applyMention(filteredOptions[mention.selected].refNo);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMention(null);
          return;
        }
      }
    };

    return (
      <div className={`relative ${wrapperClassName ?? ''}`}>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onKeyUp={detectMention}
          onClick={detectMention}
          onBlur={() => {
            // 延迟关闭，让下拉项的 click 先触发（onPointerDown 已阻止焦点转移）
            window.setTimeout(() => setMention(null), 120);
          }}
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            document.execCommand('insertText', false, text);
          }}
          className={`${className ?? ''} w-full overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-light-border bg-white p-2 text-sm text-light-text outline-none focus:border-blue-400`}
          style={{ minHeight: 64 }}
        />
        {!value && (
          <div className="pointer-events-none absolute left-2 top-2 text-xs text-light-muted">
            {placeholder}
          </div>
        )}
        {mention && filteredOptions.length > 0 && (
          <div className="absolute left-0 top-full z-40 mt-1 max-h-40 w-60 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            {filteredOptions.map((o, i) => (
              <button
                key={o.refNo}
                type="button"
                onPointerDown={(e) => e.preventDefault()}
                onMouseEnter={() => setMention((m) => (m ? { ...m, selected: i } : m))}
                onClick={() => applyMention(o.refNo)}
                className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs ${
                  i === mention.selected ? 'bg-blue-50 text-blue-700' : 'text-slate-600'
                }`}
              >
                {o.thumbnail ? (
                  <img src={o.thumbnail} alt="" className="h-6 w-6 rounded border border-slate-100 object-cover" />
                ) : (
                  <span className="grid h-6 w-6 place-items-center rounded border border-slate-100 text-[10px] text-slate-400">
                    图
                  </span>
                )}
                <span className="truncate">
                  @图{o.refNo}
                  {o.isGroup && o.count && o.count > 1 && (
                    <span className="ml-1 text-[10px] text-slate-400">（组 {o.count} 张）</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);
